import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, Input, InputGroup, InputLeftElement,
    Image, Link as ChakraLink, Select, SimpleGrid, Skeleton, Table, Tbody, Td, Text, Th, Thead,
    Tooltip, Tr, Tabs, TabList, TabPanel, TabPanels, Tab, useBreakpointValue, useToast, VStack,
    FormControl, FormLabel, Switch, Divider,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    FaMotorcycle, FaCarSide, FaTruck, FaShuttleVan, FaSearch, FaMoneyBillWave, FaRoute,
    FaExclamationTriangle, FaBoxOpen, FaListUl, FaCog, FaStore, FaPlug, FaLink, FaWallet, FaPlus, FaBolt,
    FaMapMarkerAlt, FaWarehouse,
} from 'react-icons/fa';
import ScrollToTop from '../components/ScrollToTop';
import BuscaVenda from '../components/BuscaVenda';
import ModalSolicitacaoLalamove from '../components/ModalSolicitacaoLalamove';
import AbaPontosColeta from '../components/LalamoveColeta/AbaPontosColeta';
import ModalColetaLalamove from '../components/LalamoveColeta/ModalColetaLalamove';
import ModalVincularColeta from '../components/LalamoveColeta/ModalVincularColeta';
import ConfigGorjeta from '../components/GorjetaLalamove/ConfigGorjeta';
import ConfigTextoColeta from '../components/LalamoveColeta/ConfigTextoColeta';
import { MODELO_PADRAO as TEXTO_COLETA_PADRAO, problemaDoModelo } from '../components/LalamoveColeta/texto';
import { conferir as conferirGorjeta, normalizar as normalizarGorjeta } from '../components/GorjetaLalamove/regras';
import {
    getCorridasLalamove, getLalamoveServicos, getLogsLalamove,
    getConfiguracaoLalamove, salvarConfiguracaoLalamove, testarConexaoLalamove, importarCorridaLalamove,
} from '../services/api';

/** Mesmos rotulos de LalamoveService::ROTULOS, para o operador informar a
 *  modalidade de corridas criadas no portal da Lalamove. */
const ROTULO_SERVICO = {
    LALAGO: 'Moto',
    LALAPRO: 'Moto Pro',
    HATCHBACK: 'Hatch',
    CAR: 'Carro',
    UV_FIORINO: 'Fiorino',
    VAN: 'Van',
    TRUCK330: 'Caminhão 3/4',
    TRUCK3_5T: 'Caminhão 3,5t',
};

const ICONE_SERVICO = {
    LALAGO: FaMotorcycle,
    LALAPRO: FaMotorcycle,
    HATCHBACK: FaCarSide,
    CAR: FaCarSide,
    UV_FIORINO: FaShuttleVan,
    VAN: FaShuttleVan,
    TRUCK330: FaTruck,
    TRUCK3_5T: FaTruck,
};

const CORES_STATUS = {
    PENDENTE: 'gray',
    ASSIGNING_DRIVER: 'yellow',
    ON_GOING: 'blue',
    PICKED_UP: 'purple',
    COMPLETED: 'green',
    CANCELED: 'red',
    EXPIRED: 'red',
    REJECTED: 'red',
};

const brl = (valor) => `R$ ${Number(valor || 0).toFixed(2).replace('.', ',')}`;

const dataHora = (valor) => {
    if (!valor) return '-';
    const d = new Date(valor);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function Kpi({ titulo, valor, cor = 'gray.800', icone, ajuda }) {
    return (
        <Box bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4} shadow="sm">
            <HStack spacing={2} mb={1}>
                {icone && <Icon as={icone} color="gray.400" boxSize={3} />}
                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                    {titulo}
                </Text>
            </HStack>
            <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={cor} lineHeight="1.2" whiteSpace="nowrap">{valor}</Text>
            {ajuda && <Text fontSize="xs" color="gray.400" mt={1}>{ajuda}</Text>}
        </Box>
    );
}

const CAMPOS_CONFIG = ['origem_endereco', 'origem_nome', 'origem_telefone', 'origem_lat', 'origem_lng'];

function AbaConfiguracao({ ativa }) {
    const toast = useToast();
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [testando, setTestando] = useState(false);
    const [dados, setDados] = useState(null);
    const [form, setForm] = useState({});
    const [servicos, setServicos] = useState([]);
    const [cidade, setCidade] = useState(null);
    const [ativos, setAtivos] = useState([]);
    const [erroServicos, setErroServicos] = useState(null);
    const [gorjeta, setGorjeta] = useState(null);
    // Texto padrao do motorista na coleta de compra (#2307).
    const [textoColeta, setTextoColeta] = useState('');
    const [textoColetaPadrao, setTextoColetaPadrao] = useState(TEXTO_COLETA_PADRAO);
    const [salvo, setSalvo] = useState(null);

    // O que esta na tela contra o que veio do servidor: sem isso o operador
    // sai da aba achando que salvou.
    const retrato = JSON.stringify({ form, ativos: [...ativos].sort(), gorjeta: gorjeta && normalizarGorjeta(gorjeta), textoColeta: textoColeta.trim() });
    const problemaTextoColeta = problemaDoModelo(textoColeta);
    const alterado = salvo !== null && retrato !== salvo;
    const { erros: errosGorjeta, avisos: avisosGorjeta } = conferirGorjeta(gorjeta);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const res = await getConfiguracaoLalamove();
            setDados(res);
            const inicial = {};
            CAMPOS_CONFIG.forEach((c) => {
                inicial[c] = res.config?.[c] ?? res.padrao_env?.[c] ?? '';
            });
            setForm(inicial);
            setAtivos(res.config?.servicos_ativos || []);
            const regras = normalizarGorjeta(res.config?.aumento_gorjeta);
            setGorjeta(regras);
            const padraoColeta = res.config?.texto_coleta_padrao || TEXTO_COLETA_PADRAO;
            const texto = res.config?.texto_coleta || padraoColeta;
            setTextoColetaPadrao(padraoColeta);
            setTextoColeta(texto);
            setSalvo(JSON.stringify({ form: inicial, ativos: [...(res.config?.servicos_ativos || [])].sort(), gorjeta: regras, textoColeta: texto.trim() }));
        } catch (e) {
            toast({ title: 'Falha ao carregar a configuração', status: 'error', isClosable: true });
        } finally {
            setCarregando(false);
        }
    }, [toast]);

    useEffect(() => { if (ativa && !dados) carregar(); }, [ativa, dados, carregar]);

    useEffect(() => {
        if (!ativa || servicos.length > 0 || erroServicos) return;
        getLalamoveServicos()
            .then((res) => { setServicos(res.servicos || []); setCidade(res.cidade || null); })
            .catch((e) => setErroServicos(e.response?.data?.error || 'Não foi possível carregar as modalidades da Lalamove.'));
    }, [ativa, servicos.length, erroServicos]);

    const alternar = (serviceType) => {
        setAtivos((lista) => (lista.includes(serviceType)
            ? lista.filter((s) => s !== serviceType)
            : [...lista, serviceType]));
    };

    const salvar = async () => {
        if (problemaTextoColeta) {
            toast({ title: 'Confira o texto padrão do motorista', description: problemaTextoColeta, status: 'warning', duration: 7000, isClosable: true });
            return;
        }

        if (errosGorjeta.length > 0) {
            toast({ title: 'Confira o aumento da gorjeta', description: errosGorjeta[0], status: 'warning', duration: 7000, isClosable: true });
            return;
        }

        setSalvando(true);
        try {
            const payload = {
                ...form,
                servicos_ativos: ativos,
                aumento_gorjeta: normalizarGorjeta(gorjeta),
                // Igual ao de fabrica vai em branco: o servidor guarda nulo e segue o padrao.
                texto_coleta: textoColeta.trim() === textoColetaPadrao ? '' : textoColeta,
            };
            CAMPOS_CONFIG.forEach((c) => { if (payload[c] === '') payload[c] = null; });

            const res = await salvarConfiguracaoLalamove(payload);
            toast({
                title: 'Configuração salva',
                description: res?.alterados?.includes('aumento_gorjeta')
                    ? 'O novo aumento da gorjeta vale para as próximas corridas.'
                    : undefined,
                status: 'success',
                isClosable: true,
            });
            setDados(null);
        } catch (e) {
            const erros = e.response?.data?.errors;
            toast({
                title: 'Não foi possível salvar',
                description: erros ? Object.values(erros).flat().join(' ') : e.response?.data?.error,
                status: 'error',
                isClosable: true,
            });
        } finally {
            setSalvando(false);
        }
    };

    const testar = async () => {
        setTestando(true);
        try {
            const res = await testarConexaoLalamove();
            toast({
                title: 'Conexão OK',
                description: `${res.cidade || 'Cidade encontrada'} com ${res.total_servicos} modalidade(s).`,
                status: 'success',
                isClosable: true,
            });
        } catch (e) {
            toast({ title: 'Falha na conexão', description: e.response?.data?.error, status: 'error', isClosable: true });
        } finally {
            setTestando(false);
        }
    };

    const campo = (nome, label, extras = {}) => (
        <FormControl>
            <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>{label}</FormLabel>
            <Input
                size="sm"
                borderRadius="lg"
                value={form[nome] ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, [nome]: e.target.value }))}
                {...extras}
            />
        </FormControl>
    );

    if (carregando) {
        return <VStack spacing={2}>{[...Array(5)].map((_, i) => <Skeleton key={i} height="42px" w="100%" borderRadius="md" />)}</VStack>;
    }

    const conectado = dados?.em_uso?.configurado;

    return (
        <VStack align="stretch" spacing={5}>
            <Flex align="center" gap={3} wrap="wrap">
                <Badge colorScheme={conectado ? 'green' : 'red'} borderRadius="full" px={3} py={1}>
                    {conectado ? 'Credenciais ativas' : 'Sem credencial'}
                </Badge>
                <Button size="xs" leftIcon={<Icon as={FaPlug} boxSize={3} />} variant="outline" onClick={testar} isLoading={testando}>
                    Testar conexão
                </Button>
                {dados?.config?.atualizado_em && (
                    <Text fontSize="xs" color="gray.400">
                        Alterada em {dataHora(dados.config.atualizado_em)}
                        {dados.config.atualizado_por ? ` por ${dados.config.atualizado_por}` : ''}
                    </Text>
                )}
            </Flex>

            <Box>
                <HStack spacing={2} mb={3}>
                    <Icon as={FaStore} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Endereço de origem (loja)</Text>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box gridColumn={{ md: 'span 2' }}>{campo('origem_endereco', 'Endereço completo')}</Box>
                    {campo('origem_nome', 'Nome do remetente')}
                    {campo('origem_telefone', 'Telefone', { placeholder: '+551152396484' })}
                    {campo('origem_lat', 'Latitude', { placeholder: 'opcional' })}
                    {campo('origem_lng', 'Longitude', { placeholder: 'opcional' })}
                </SimpleGrid>
                <Text fontSize="xs" color="gray.400" mt={2}>
                    Latitude e longitude são opcionais: sem elas a Lalamove localiza o ponto pelo endereço.
                    Se preencher, use o ponto exato da loja: coordenada errada muda o preço da corrida.
                </Text>
            </Box>

            <Divider />

            <Box>
                <HStack spacing={2} mb={1}>
                    <Icon as={FaMotorcycle} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Modalidades</Text>
                </HStack>
                <Text fontSize="xs" color="gray.400" mb={3}>
                    Só as ligadas são cotadas na Gestão de Despacho. A modalidade que o cliente escolheu no
                    checkout é sempre cotada, mesmo desligada. Cobertura: <b>{cidade || 'São Paulo'}</b>.
                </Text>

                {erroServicos ? (
                    <Alert status="warning" borderRadius="lg" fontSize="sm"><AlertIcon />{erroServicos}</Alert>
                ) : servicos.length === 0 ? (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                        {[...Array(4)].map((_, i) => <Skeleton key={i} height="86px" borderRadius="lg" />)}
                    </SimpleGrid>
                ) : (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                        {servicos.map((s) => {
                            const ligado = ativos.includes(s.service_type);
                            return (
                                <Box key={s.service_type} borderWidth="1px" borderRadius="lg" p={3}
                                    borderColor={ligado ? 'purple.200' : 'gray.100'} bg={ligado ? 'purple.50' : 'gray.50'}>
                                    <Flex align="center" justify="space-between" mb={2}>
                                        <HStack spacing={2}>
                                            <Icon as={ICONE_SERVICO[s.service_type] || FaTruck}
                                                color={ligado ? 'purple.500' : 'gray.400'} boxSize={4} />
                                            <Text fontWeight="bold" fontSize="sm" color={ligado ? 'gray.700' : 'gray.500'}>{s.rotulo}</Text>
                                        </HStack>
                                        <Switch size="sm" colorScheme="purple" isChecked={ligado}
                                            onChange={() => alternar(s.service_type)} />
                                    </Flex>
                                    <Text fontSize="xs" color="gray.500">{s.service_type}</Text>
                                    {s.dimensoes && (
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            cabe até {s.dimensoes.comprimento_m} x {s.dimensoes.largura_m} x {s.dimensoes.altura_m} m
                                        </Text>
                                    )}
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                )}

                {servicos.length > 0 && ativos.length === 0 && (
                    <Alert status="error" borderRadius="lg" fontSize="sm" mt={3}>
                        <AlertIcon />Deixe pelo menos uma modalidade ligada.
                    </Alert>
                )}
            </Box>

            <Divider />

            <ConfigGorjeta valor={gorjeta} onChange={setGorjeta} avisos={avisosGorjeta} />

            <Divider />

            <ConfigTextoColeta
                valor={textoColeta}
                onChange={setTextoColeta}
                padrao={textoColetaPadrao}
                variaveis={dados?.config?.texto_coleta_variaveis}
            />

            <Flex
                justify="flex-end"
                align="center"
                gap={2}
                wrap="wrap"
                position="sticky"
                bottom={0}
                bg="white"
                py={3}
                borderTopWidth={alterado ? '1px' : 0}
                zIndex={1}
            >
                {alterado && (
                    <Text fontSize="xs" color="orange.600" fontWeight="bold" flex={{ md: 1 }} w={{ base: '100%', md: 'auto' }}>
                        Alterações não salvas
                    </Text>
                )}
                <HStack spacing={2} w={{ base: '100%', md: 'auto' }} justify="flex-end">
                    <Button size="sm" variant="outline" onClick={() => setDados(null)} isDisabled={salvando || !alterado}
                        flex={{ base: 1, md: 'initial' }} minH="40px">Descartar</Button>
                    <Button size="sm" colorScheme="purple" onClick={salvar} isLoading={salvando} loadingText="Salvando"
                        isDisabled={(servicos.length > 0 && ativos.length === 0) || errosGorjeta.length > 0 || !!problemaTextoColeta}
                        flex={{ base: 2, md: 'initial' }} minH="40px">Salvar configuração</Button>
                </HStack>
            </Flex>
        </VStack>
    );
}

export default function Lalamove() {
    const toast = useToast();
    const compacto = useBreakpointValue({ base: true, xl: false });

    const [carregando, setCarregando] = useState(true);
    const [corridas, setCorridas] = useState([]);
    const [resumo, setResumo] = useState(null);
    const [rotulos, setRotulos] = useState({});
    const [carteira, setCarteira] = useState(null);
    const [erro, setErro] = useState(null);

    const [filtros, setFiltros] = useState({ status: 'todos', service_type: 'todas', resultado: '', busca: '', de: '', ate: '', finalidade: '' });

    const [abaAtiva, setAbaAtiva] = useState(0);

    const importar = useDisclosure();
    // Mesma tela do despacho, aberta sem venda: o operador escolhe dentro dela
    // se parte de uma venda ou preenche tudo a mao.
    const avulsa = useDisclosure();
    const [formImportar, setFormImportar] = useState({ order_id: '', venda_id: '', service_type: '' });
    const [importando, setImportando] = useState(false);
    const [vendaEscolhida, setVendaEscolhida] = useState(null);
    // Coleta de compra no fornecedor (#2307): corrida nova (ordemId null) ou existente.
    const [coleta, setColeta] = useState(null);
    // Vincular compras a uma corrida: a da linha da tabela, ou {} para importar pelo codigo.
    const [vinculo, setVinculo] = useState(null);

    const [logs, setLogs] = useState([]);
    const [soErros, setSoErros] = useState(false);
    const [carregandoLogs, setCarregandoLogs] = useState(false);

    const carregarCorridas = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            const params = {};
            Object.entries(filtros).forEach(([k, v]) => {
                if (v && v !== 'todos' && v !== 'todas') params[k] = v;
            });

            const res = await getCorridasLalamove(params);
            setCorridas(res.corridas || []);
            setResumo(res.resumo || null);
            setRotulos(res.rotulos || {});
            setCarteira(res.carteira || null);
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível carregar as corridas.');
        } finally {
            setCarregando(false);
        }
    }, [filtros]);

    useEffect(() => { carregarCorridas(); }, [carregarCorridas]);

    const carregarLogs = async (apenasErros = soErros) => {
        setCarregandoLogs(true);
        try {
            const res = await getLogsLalamove({ limite: 100, so_erros: apenasErros ? 1 : undefined });
            setLogs(res.logs || []);
        } catch (e) {
            toast({ title: 'Falha ao carregar o log', status: 'error', isClosable: true });
        } finally {
            setCarregandoLogs(false);
        }
    };

    const corDoSaldo = resumo && resumo.saldo < 0 ? 'red.600' : 'green.600';

    const escolherVenda = (v) => {
        setVendaEscolhida(v);
        setFormImportar((f) => ({ ...f, venda_id: String(v.id) }));
    };

    const limparVenda = () => {
        setVendaEscolhida(null);
        setFormImportar((f) => ({ ...f, venda_id: '' }));
    };

    const fecharImportar = () => {
        setFormImportar({ order_id: '', venda_id: '', service_type: '' });
        limparVenda();
        importar.onClose();
    };

    const enviarImportacao = async () => {
        const orderId = formImportar.order_id.trim();
        if (!orderId) return;

        setImportando(true);
        try {
            const res = await importarCorridaLalamove(
                orderId,
                formImportar.venda_id.trim(),
                formImportar.service_type || undefined,
            );
            toast({
                title: 'Corrida vinculada',
                description: `${res.ordem?.rotulo || res.ordem?.service_type} em ${res.ordem?.status_rotulo}.`,
                status: 'success',
                isClosable: true,
            });
            fecharImportar();
            carregarCorridas();
        } catch (e) {
            toast({
                title: 'Não foi possível vincular',
                description: e.response?.data?.error || 'Erro inesperado.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            setImportando(false);
        }
    };

    const carteiraVelha = !!carteira?.atualizado_em && (carteira.dias_sem_atualizar ?? 0) >= 7;
    const saldoBaixo = carteira?.saldo !== null && carteira?.saldo !== undefined && carteira.saldo < 100;

    const modalImportar = (
        <Modal isOpen={importar.isOpen} onClose={fecharImportar} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader fontSize="md">Vincular corrida da Lalamove</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.600">
                            Para corrida pedida fora do painel. O resto dos dados (modalidade, preço, status,
                            motorista e trajeto) vem da própria Lalamove.
                        </Text>
                        <Alert status="info" borderRadius="lg" fontSize="xs" py={2}>
                            <AlertIcon />
                            <Flex justify="space-between" align="center" gap={2} w="100%" wrap="wrap">
                                <Text>Foi uma coleta de compra no fornecedor?</Text>
                                <Button size="xs" colorScheme="teal" variant="outline" onClick={() => { fecharImportar(); setVinculo({ ordem: null }); }}>
                                    Vincular a compras
                                </Button>
                            </Flex>
                        </Alert>
                        <FormControl>
                            <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>
                                Código da corrida
                            </FormLabel>
                            <Input
                                size="sm"
                                borderRadius="lg"
                                autoFocus
                                placeholder="o mesmo que aparece na Lalamove"
                                value={formImportar.order_id}
                                onChange={(e) => setFormImportar((f) => ({ ...f, order_id: e.target.value }))}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>
                                Modalidade
                            </FormLabel>
                            <Select
                                size="sm"
                                borderRadius="lg"
                                placeholder="não sei / deixar em branco"
                                value={formImportar.service_type}
                                onChange={(e) => setFormImportar((f) => ({ ...f, service_type: e.target.value }))}
                            >
                                {Object.entries(ROTULO_SERVICO).map(([chave, rotulo]) => (
                                    <option key={chave} value={chave}>{rotulo}</option>
                                ))}
                            </Select>
                            <Text fontSize="xs" color="gray.400" mt={1}>
                                A Lalamove não informa isso. Em branco, fica &ldquo;não informada&rdquo;.
                            </Text>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>
                                Venda (opcional)
                            </FormLabel>

                            <BuscaVenda
                                ativa={importar.isOpen}
                                escolhida={vendaEscolhida}
                                onEscolher={escolherVenda}
                                onLimpar={limparVenda}
                            />

                            <Text fontSize="xs" color="gray.400" mt={2}>
                                Sem a venda a corrida entra só para acompanhamento, sem comparar com o frete cobrado.
                            </Text>
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button size="sm" variant="ghost" onClick={fecharImportar} isDisabled={importando}>Cancelar</Button>
                    <Button
                        size="sm"
                        colorScheme="purple"
                        onClick={enviarImportacao}
                        isLoading={importando}
                        isDisabled={!formImportar.order_id.trim()}
                    >
                        Vincular
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );

    // Coleta de compra (#2307) nao tem venda nem frete cobrado: a linha mostra o
    // fornecedor e os pedidos, e a margem fica em branco em vez de "+R$ 0,00".
    const ehColeta = (c) => c.finalidade === 'coleta';
    const origemDaCorrida = (c) => {
        if (ehColeta(c)) {
            return {
                titulo: `Coleta · ${c.ponto_coleta_nome || 'fornecedor'}`,
                sub: (c.compras || []).map((x) => x.rotulo).join(', ') || 'sem pedido vinculado',
            };
        }
        if (c.venda_id) return { titulo: `#${c.venda_id}`, sub: c.venda_origem_id || c.venda_origem || '-' };
        return { titulo: 'Avulsa', sub: 'sem venda' };
    };
    const margemDaCorrida = (c) => (c.margem === null || c.margem === undefined
        ? <Text fontSize="xs" color="gray.400">-</Text>
        : (
            <Badge colorScheme={c.margem >= 0 ? 'green' : 'red'} fontSize="11px" px={2} borderRadius="md">
                {c.margem >= 0 ? '+' : ''}{brl(c.margem)}
            </Badge>
        ));
    const acoesDaCorrida = (c) => {
        if (ehColeta(c)) {
            return (
                <Button size="xs" variant="ghost" colorScheme="teal" onClick={() => setColeta({ ordemId: c.id })}>
                    Abrir
                </Button>
            );
        }
        if (!c.venda_id) {
            return (
                <Tooltip label="Foi coleta de compra? Ligue aos pedidos" hasArrow>
                    <Button size="xs" variant="ghost" colorScheme="teal" onClick={() => setVinculo({ ordem: c })}>
                        Vincular compra
                    </Button>
                </Tooltip>
            );
        }
        return null;
    };

    const linhaCorrida = (c) => (
        <Tr key={c.id} _hover={{ bg: 'gray.50' }}>
            <Td>
                <VStack align="start" spacing={0}>
                    <HStack spacing={1}>
                        {ehColeta(c) && <Icon as={FaWarehouse} color="teal.500" boxSize={3} />}
                        <Text fontSize="sm" fontWeight="bold" color="gray.800" whiteSpace="nowrap">{origemDaCorrida(c).titulo}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{origemDaCorrida(c).sub}</Text>
                </VStack>
            </Td>
            <Td minW="160px">
                <Text fontSize="sm">{ehColeta(c) ? 'Loja TAGA' : (c.cliente_nome || '-')}</Text>
            </Td>
            <Td>
                <HStack spacing={2}>
                    <Icon as={ICONE_SERVICO[c.service_type] || FaTruck} color="gray.400" boxSize={3} />
                    <Text fontSize="sm">{c.rotulo}</Text>
                    {c.prioridade && (
                        <Tooltip label="Pedida com prioridade">
                            <Badge colorScheme="orange" variant="solid" fontSize="9px" borderRadius="full" px={1.5}>
                                <Icon as={FaBolt} boxSize={2} />
                            </Badge>
                        </Tooltip>
                    )}
                </HStack>
            </Td>
            <Td isNumeric whiteSpace="nowrap">
                <Text fontSize="sm">{brl(c.preco_total)}</Text>
                {c.priority_fee > 0 && <Text fontSize="10px" color="gray.500">gorjeta {brl(c.priority_fee)}</Text>}
            </Td>
            <Td isNumeric whiteSpace="nowrap">
                {c.frete_cobrado === null || c.frete_cobrado === undefined
                    ? <Text fontSize="xs" color="gray.400">-</Text>
                    : <Text fontSize="sm" color="gray.600">{brl(c.frete_cobrado)}</Text>}
            </Td>
            <Td isNumeric whiteSpace="nowrap">{margemDaCorrida(c)}</Td>
            <Td>
                <Badge colorScheme={CORES_STATUS[c.status] || 'gray'} variant="subtle">{c.status_rotulo}</Badge>
            </Td>
            <Td minW="140px">
                <Text fontSize="xs" color="gray.600">{c.motorista?.nome || '-'}</Text>
                {c.motorista?.placa && <Text fontSize="10px" color="gray.400" whiteSpace="nowrap">{c.motorista.placa}</Text>}
            </Td>
            <Td>
                <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{dataHora(c.criada_em)}</Text>
                <Text fontSize="10px" color="gray.400" whiteSpace="nowrap">{c.solicitado_por || 'Sistema'}</Text>
            </Td>
            <Td>
                <VStack align="start" spacing={1}>
                    {c.share_link && (
                        <Tooltip label="Acompanhar ao vivo">
                            <ChakraLink href={c.share_link} isExternal color="purple.600" fontSize="xs" fontWeight="bold" whiteSpace="nowrap">
                                rastrear <ExternalLinkIcon boxSize={3} />
                            </ChakraLink>
                        </Tooltip>
                    )}
                    {acoesDaCorrida(c)}
                </VStack>
            </Td>
        </Tr>
    );

    const cardCorrida = (c) => (
        <Box key={c.id} bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4} shadow="sm">
            <Flex justify="space-between" align="start" mb={2} gap={2}>
                <VStack align="start" spacing={0} flex={1} minW={0}>
                    <HStack spacing={2} wrap="wrap" rowGap={1}>
                        <Icon as={ehColeta(c) ? FaWarehouse : (ICONE_SERVICO[c.service_type] || FaTruck)} color={ehColeta(c) ? 'teal.500' : 'purple.400'} boxSize={4} />
                        <Text fontWeight="bold" fontSize="sm">{origemDaCorrida(c).titulo}</Text>
                        {!ehColeta(c) && c.venda_id && (
                            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{origemDaCorrida(c).sub}</Text>
                        )}
                        <Badge colorScheme={CORES_STATUS[c.status] || 'gray'} variant="subtle" fontSize="10px">{c.status_rotulo}</Badge>
                        {c.prioridade && (
                            <Badge colorScheme="orange" variant="solid" fontSize="9px" borderRadius="full" px={1.5}>
                                <HStack spacing={0.5}><Icon as={FaBolt} boxSize={2} /><Text as="span">Prioridade</Text></HStack>
                            </Badge>
                        )}
                    </HStack>
                    <Text fontSize="xs" color="gray.500" overflowWrap="anywhere">{ehColeta(c) ? origemDaCorrida(c).sub : (c.cliente_nome || '-')}</Text>
                </VStack>
                <Box flexShrink={0}>{margemDaCorrida(c)}</Box>
            </Flex>
            <HStack spacing={4} fontSize="xs" color="gray.600" wrap="wrap">
                <Text>custo {brl(c.preco_total)}{c.priority_fee > 0 ? ` (gorjeta ${brl(c.priority_fee)})` : ''}</Text>
                {c.frete_cobrado !== null && c.frete_cobrado !== undefined && <Text>cobrado {brl(c.frete_cobrado)}</Text>}
                <Text>{dataHora(c.criada_em)}</Text>
                {c.motorista?.nome && <Text>motorista {c.motorista.nome}</Text>}
            </HStack>
            <Text fontSize="10px" color="gray.400">pedida por {c.solicitado_por || 'Sistema'}</Text>
            <HStack spacing={3} mt={2}>
                {c.share_link && (
                    <ChakraLink href={c.share_link} isExternal color="purple.600" fontSize="xs" fontWeight="bold" display="inline-block">
                        rastrear <ExternalLinkIcon boxSize={3} />
                    </ChakraLink>
                )}
                {acoesDaCorrida(c)}
            </HStack>
        </Box>
    );

    return (
        <Box bg="gray.50" minH="100vh" pl={{ base: 0, md: '64px' }}>
            <ModalSolicitacaoLalamove
                isOpen={avulsa.isOpen}
                onClose={avulsa.onClose}
                vendaId={null}
                avulsa
                onOrdemCriada={() => carregarCorridas()}
                onColetaCompra={() => { avulsa.onClose(); setColeta({ ordemId: null }); }}
            />

            <ModalColetaLalamove
                isOpen={!!coleta}
                onClose={() => setColeta(null)}
                ordemId={coleta?.ordemId || null}
                onMudou={() => carregarCorridas()}
            />

            <ModalVincularColeta
                isOpen={!!vinculo}
                onClose={() => setVinculo(null)}
                ordem={vinculo?.ordem || null}
                onVinculado={() => carregarCorridas()}
            />

            {/* Na Configuracao a barra de salvar fica presa no rodape: o botao
                de voltar ao topo sobe para nao cobrir o "Salvar". */}
            <ScrollToTop bottom={abaAtiva === 2 ? '84px' : '4'} />
            {modalImportar}

            <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={{ base: 4, md: 8 }} py={4}>
                <Flex align="center" justify="space-between" maxW="1600px" mx="auto" gap={3} wrap="wrap">
                    <Flex align="center" gap={3} pl={{ base: '56px', md: 0 }}>
                        <Box bg="white" borderWidth="1px" borderColor="gray.100" px={3} py={2} borderRadius="lg" display={{ base: 'none', sm: 'block' }}>
                            <Image src="/lalamove_logo.webp" alt="Lalamove" h="22px" w="auto" objectFit="contain" />
                        </Box>
                        <Box>
                            <Text fontSize="lg" fontWeight="bold" color="gray.800">Painel de Corridas</Text>
                            <Text fontSize="xs" color="gray.500">Custo real e margem do frete expresso</Text>
                        </Box>
                    </Flex>
                    <HStack spacing={2} wrap="wrap" w={{ base: '100%', md: 'auto' }}>
                        <Button size="sm" leftIcon={<Icon as={FaPlus} boxSize={3} />} onClick={avulsa.onOpen} colorScheme="purple">
                            Gerar corrida avulsa
                        </Button>
                        <Button size="sm" leftIcon={<Icon as={FaWarehouse} boxSize={3} />} onClick={() => setColeta({ ordemId: null })} colorScheme="teal" variant="outline">
                            Coletar compra
                        </Button>
                        <Button size="sm" leftIcon={<Icon as={FaLink} boxSize={3} />} onClick={importar.onOpen} variant="outline">
                            Vincular corrida
                        </Button>
                        <Button size="sm" leftIcon={<RepeatIcon />} onClick={carregarCorridas} isLoading={carregando} variant="outline">
                            Atualizar
                        </Button>
                    </HStack>
                </Flex>
            </Box>

            <Box maxW="1600px" mx="auto" px={{ base: 4, md: 8 }} py={6}>
                {erro && (
                    <Alert status="error" mb={5} borderRadius="xl" variant="left-accent">
                        <AlertIcon />{erro}
                    </Alert>
                )}

                {carteira && (
                    <Flex
                        align="center"
                        justify="space-between"
                        gap={3}
                        wrap="wrap"
                        bg="white"
                        borderWidth="1px"
                        borderColor={carteiraVelha ? 'orange.200' : 'gray.100'}
                        borderRadius="xl"
                        px={4}
                        py={3}
                        mb={4}
                        shadow="sm"
                    >
                        <HStack spacing={3} align="baseline">
                            <Icon as={FaWallet} color={carteiraVelha ? 'orange.400' : 'gray.400'} boxSize={4} />
                            <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                                Carteira Lalamove
                            </Text>
                            <Text fontSize="xl" fontWeight="bold" color={saldoBaixo ? 'red.600' : 'gray.800'}>
                                {carteira.saldo !== null ? brl(carteira.saldo) : 'não informado'}
                            </Text>
                        </HStack>

                        <HStack spacing={3} wrap="wrap">
                            <Text fontSize="xs" color={carteiraVelha ? 'orange.600' : 'gray.400'}>
                                {carteira.atualizado_em
                                    ? `informado pela Lalamove em ${dataHora(carteira.atualizado_em)}`
                                    : 'a Lalamove ainda não informou nenhum saldo'}
                            </Text>
                            <ChakraLink
                                href="https://partner.lalamove.com"
                                isExternal
                                color="purple.600"
                                fontSize="xs"
                                fontWeight="bold"
                            >
                                conferir no portal <ExternalLinkIcon boxSize={3} />
                            </ChakraLink>
                        </HStack>
                    </Flex>
                )}

                {carteira && carteiraVelha && (
                    <Alert status="warning" mb={4} borderRadius="xl" fontSize="sm" variant="left-accent">
                        <AlertIcon />
                        Este saldo não muda desde {dataHora(carteira.atualizado_em)}. A Lalamove só avisa quando o
                        valor muda, então ou não houve movimento, ou paramos de receber os avisos dela.
                    </Alert>
                )}

                <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4} mb={6}>
                    {carregando && !resumo ? (
                        [...Array(5)].map((_, i) => <Skeleton key={i} height="96px" borderRadius="xl" />)
                    ) : (
                        <>
                            <Kpi titulo="Corridas" valor={resumo?.corridas ?? 0} icone={FaRoute} />
                            <Kpi titulo="Pago à Lalamove" valor={brl(resumo?.pago)} icone={FaMoneyBillWave} />
                            <Kpi titulo="Cobrado do cliente" valor={brl(resumo?.cobrado)} icone={FaMoneyBillWave} />
                            <Kpi titulo="Resultado" valor={brl(resumo?.saldo)} cor={corDoSaldo} icone={FaMoneyBillWave} ajuda="cobrado menos pago" />
                            <Kpi
                                titulo="No prejuízo"
                                valor={resumo?.no_prejuizo ?? 0}
                                cor={resumo?.no_prejuizo > 0 ? 'red.600' : 'gray.800'}
                                icone={FaExclamationTriangle}
                                ajuda={`${resumo?.ativas ?? 0} em andamento`}
                            />
                        </>
                    )}
                </SimpleGrid>

                <Tabs colorScheme="purple" variant="soft-rounded" onChange={(idx) => {
                    setAbaAtiva(idx);
                    if (idx === 1 && logs.length === 0) carregarLogs();
                }}>
                    {/* overflow hidden corta os cantos da tabela, mas desliga o sticky:
                        na Configuracao a barra de salvar precisa ficar presa no rodape. */}
                    <Box bg="white" borderRadius="2xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow={abaAtiva === 2 ? 'visible' : 'hidden'}>
                        <Box px={{ base: 4, md: 6 }} pt={4} pb={0} borderBottom="1px solid" borderColor="gray.100">
                            <TabList gap={1} pb={4} overflowX="auto" flexWrap={{ base: "wrap", md: "nowrap" }} sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                                <Tab fontWeight="semibold" fontSize="sm" _selected={{ bg: 'purple.600', color: 'white' }}
                                    borderRadius="lg" px={4} py={2} flexShrink={0}>
                                    <Icon as={FaRoute} mr={2} boxSize={3} />Corridas
                                    {resumo?.corridas ? (
                                        <Badge ml={2} colorScheme="purple" variant="solid" borderRadius="full" fontSize="0.6rem">
                                            {resumo.corridas}
                                        </Badge>
                                    ) : null}
                                </Tab>
                                <Tab fontWeight="semibold" fontSize="sm" _selected={{ bg: 'gray.700', color: 'white' }}
                                    borderRadius="lg" px={4} py={2} flexShrink={0}>
                                    <Icon as={FaListUl} mr={2} boxSize={3} />Log de chamadas
                                </Tab>
                                <Tab fontWeight="semibold" fontSize="sm" _selected={{ bg: 'blue.600', color: 'white' }}
                                    borderRadius="lg" px={4} py={2} flexShrink={0}>
                                    <Icon as={FaCog} mr={2} boxSize={3} />Configuração
                                </Tab>
                                <Tab fontWeight="semibold" fontSize="sm" _selected={{ bg: 'teal.600', color: 'white' }}
                                    borderRadius="lg" px={4} py={2} flexShrink={0}>
                                    <Icon as={FaMapMarkerAlt} mr={2} boxSize={3} />Endereços de coleta
                                </Tab>
                            </TabList>
                        </Box>

                    <TabPanels>
                        <TabPanel px={{ base: 2, md: 4 }}>
                            <Flex gap={2} mb={4} wrap="wrap">
                                <InputGroup size="sm" maxW={{ base: '100%', md: '260px' }}>
                                    <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" boxSize={3} /></InputLeftElement>
                                    <Input
                                        placeholder="Venda, pedido, cliente ou ordem"
                                        borderRadius="lg"
                                        value={filtros.busca}
                                        onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))}
                                    />
                                </InputGroup>
                                <Select size="sm" maxW={{ base: '48%', md: '190px' }} borderRadius="lg" value={filtros.finalidade}
                                    onChange={(e) => setFiltros((f) => ({ ...f, finalidade: e.target.value }))}>
                                    <option value="">Todas as origens</option>
                                    <option value="venda">Só entregas de venda</option>
                                    <option value="coleta">Só coletas de compra</option>
                                    <option value="avulsa">Só avulsas</option>
                                </Select>
                                <Select size="sm" maxW={{ base: '48%', md: '170px' }} borderRadius="lg" value={filtros.status}
                                    onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}>
                                    <option value="todos">Todos os status</option>
                                    <option value="ativas">Em andamento</option>
                                    <option value="COMPLETED">Entregues</option>
                                    <option value="CANCELED">Canceladas</option>
                                    <option value="EXPIRED">Expiradas</option>
                                </Select>
                                <Select size="sm" maxW={{ base: '48%', md: '200px' }} borderRadius="lg" value={filtros.service_type}
                                    onChange={(e) => setFiltros((f) => ({ ...f, service_type: e.target.value }))}>
                                    <option value="todas">Todas as modalidades</option>
                                    {Object.entries(rotulos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </Select>
                                <Select size="sm" maxW={{ base: '48%', md: '180px' }} borderRadius="lg" value={filtros.resultado}
                                    onChange={(e) => setFiltros((f) => ({ ...f, resultado: e.target.value }))}>
                                    <option value="">Qualquer resultado</option>
                                    <option value="prejuizo">Só prejuízo</option>
                                    <option value="lucro">Só positivo</option>
                                </Select>
                                <Input size="sm" type="date" maxW={{ base: '48%', md: '150px' }} borderRadius="lg" value={filtros.de}
                                    onChange={(e) => setFiltros((f) => ({ ...f, de: e.target.value }))} />
                                <Input size="sm" type="date" maxW={{ base: '48%', md: '150px' }} borderRadius="lg" value={filtros.ate}
                                    onChange={(e) => setFiltros((f) => ({ ...f, ate: e.target.value }))} />
                            </Flex>

                            {carregando ? (
                                <VStack spacing={2}>{[...Array(4)].map((_, i) => <Skeleton key={i} height="52px" w="100%" borderRadius="md" />)}</VStack>
                            ) : corridas.length === 0 ? (
                                <VStack py={10} spacing={2}>
                                    <Icon as={FaBoxOpen} boxSize={8} color="gray.300" />
                                    <Text color="gray.500" fontSize="sm">Nenhuma corrida Lalamove encontrada.</Text>
                                    <Text color="gray.400" fontSize="xs">As corridas aparecem aqui assim que forem pedidas na Gestão de Despacho ou nas telas de compras.</Text>
                                </VStack>
                            ) : compacto ? (
                                <VStack align="stretch" spacing={3}>{corridas.map(cardCorrida)}</VStack>
                            ) : (
                                <Box overflowX="auto">
                                    <Table size="sm" variant="simple" sx={{ "th, td": { px: 2 } }}>
                                        <Thead>
                                            <Tr>
                                                <Th>Origem</Th>
                                                <Th>Destino</Th>
                                                <Th>Modalidade</Th>
                                                <Th isNumeric>Custo</Th>
                                                <Th isNumeric>Cobrado</Th>
                                                <Th isNumeric>Margem</Th>
                                                <Th>Status</Th>
                                                <Th>Motorista</Th>
                                                <Th>Criada</Th>
                                                <Th />
                                            </Tr>
                                        </Thead>
                                        <Tbody>{corridas.map(linhaCorrida)}</Tbody>
                                    </Table>
                                </Box>
                            )}
                        </TabPanel>

                        <TabPanel px={{ base: 2, md: 4 }}>
                            <HStack mb={3} spacing={2}>
                                <Button size="xs" variant={soErros ? 'solid' : 'outline'} colorScheme="red"
                                    onClick={() => { const v = !soErros; setSoErros(v); carregarLogs(v); }}>
                                    Só erros
                                </Button>
                                <Button size="xs" leftIcon={<RepeatIcon />} variant="outline" onClick={() => carregarLogs()} isLoading={carregandoLogs}>
                                    Atualizar
                                </Button>
                            </HStack>

                            {carregandoLogs ? (
                                <VStack spacing={2}>{[...Array(4)].map((_, i) => <Skeleton key={i} height="36px" w="100%" borderRadius="md" />)}</VStack>
                            ) : logs.length === 0 ? (
                                <Text color="gray.500" fontSize="sm" py={6} textAlign="center">Nenhuma chamada registrada.</Text>
                            ) : (
                                <Box overflowX="auto">
                                    <Table size="sm" variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th>Quando</Th>
                                                <Th>Usuário</Th>
                                                <Th>Ação</Th>
                                                <Th>Endpoint</Th>
                                                <Th>HTTP</Th>
                                                <Th>Venda</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {logs.map((l) => (
                                                <Tr key={l.id} _hover={{ bg: 'gray.50' }}>
                                                    <Td><Text fontSize="xs" color="gray.500">{dataHora(l.created_at)}</Text></Td>
                                                    <Td><Text fontSize="xs" color="gray.600">{l.user_name || 'Sistema'}</Text></Td>
                                                    <Td><Text fontSize="xs">{l.acao || '-'}</Text></Td>
                                                    <Td><Text fontSize="xs" color="gray.600" fontFamily="mono">{l.metodo} {l.endpoint}</Text></Td>
                                                    <Td>
                                                        <Badge colorScheme={Number(l.http_code) >= 200 && Number(l.http_code) < 300 ? 'green' : 'red'} fontSize="10px">
                                                            {l.http_code}
                                                        </Badge>
                                                    </Td>
                                                    <Td><Text fontSize="xs" color="gray.500">{l.venda_id ? `#${l.venda_id}` : '-'}</Text></Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Box>
                            )}
                        </TabPanel>

                        <TabPanel px={{ base: 2, md: 4 }}>
                            <AbaConfiguracao ativa={abaAtiva === 2} />
                        </TabPanel>

                        <TabPanel px={{ base: 2, md: 4 }}>
                            <AbaPontosColeta ativa={abaAtiva === 3} />
                        </TabPanel>
                    </TabPanels>
                    </Box>
                </Tabs>
            </Box>
        </Box>
    );
}
